import { Controller } from "@hotwired/stimulus"

const BOOK_RATE = 0.70
const SHOW_RATE = 0.60
const CLOSE_RATE = 0.50
/** Spread estimated lead budget evenly over this many days for a daily ad-spend figure. */
const AD_SPEND_DAYS = 30

export default class extends Controller {
  static targets = [
    "gapDisplay",
    "membersNeeded",
    "showsNeeded",
    "bookedNeeded",
    "leadsNeeded",
    "cplSlider",
    "cplDisplay",
    "adSpendDisplay",
    "adSpendNote",
    "funnelMembers",
    "funnelShows",
    "funnelBooked",
    "funnelLeads",
    "modeToggle",
    "heroHeadline",
    "heroSub"
  ]

  static values = {
    gap: { type: Number, default: 10 },
    target: { type: Number, default: 0 },
    current: { type: Number, default: 0 },
    cpl: { type: Number, default: 12 },
    mode: { type: String, default: "gap" }
  }

  connect() {
    this.recalculate()
    this.updateToggleUI()
  }

  setMode(event) {
    this.modeValue = event.currentTarget.dataset.mode
    this.recalculate()
    this.updateToggleUI()
  }

  cplChanged(event) {
    this.cplValue = parseInt(event.target.value, 10) || 12
    this.recalculate()
  }

  get activeMemberCount() {
    if (this.modeValue === "total" && this.targetValue > 0) {
      return this.targetValue
    }
    return Math.max(this.gapValue, 1)
  }

  recalculate() {
    const members = this.activeMemberCount
    const shows = Math.ceil(members / CLOSE_RATE)
    const booked = Math.ceil(shows / SHOW_RATE)
    const leads = Math.ceil(booked / BOOK_RATE)
    const cpl = Math.max(this.cplValue, 5)
    const adSpendTotal = leads * cpl
    const adSpendPerDay = adSpendTotal / AD_SPEND_DAYS

    const fmt = (n) =>
      new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(n)
    const fmtUsd = (n) =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0
      }).format(n)
    this.setText(this.gapDisplayTargets, fmt(members))
    this.setText(this.membersNeededTargets, fmt(members))
    this.setText(this.showsNeededTargets, fmt(shows))
    this.setText(this.bookedNeededTargets, fmt(booked))
    this.setText(this.leadsNeededTargets, fmt(leads))
    this.setText(this.cplDisplayTargets, fmtUsd(cpl))
    this.setText(
      this.adSpendDisplayTargets,
      `${fmtUsd(Math.round(adSpendPerDay))}/day`
    )

    if (this.hasAdSpendNoteTarget) {
      this.adSpendNoteTarget.textContent =
        `${fmt(leads)} leads \u00D7 ${fmtUsd(cpl)} \u2248 ${fmtUsd(adSpendTotal)} over ${AD_SPEND_DAYS} days`
    }

    if (this.hasCplSliderTarget && this.cplSliderTarget.value !== String(cpl)) {
      this.cplSliderTarget.value = String(cpl)
    }

    const maxBar = leads || 1
    this.setWidth(this.funnelLeadsTargets, 100)
    this.setWidth(this.funnelBookedTargets, (booked / maxBar) * 100)
    this.setWidth(this.funnelShowsTargets, (shows / maxBar) * 100)
    this.setWidth(this.funnelMembersTargets, (members / maxBar) * 100)

    if (this.hasHeroHeadlineTarget) {
      if (this.modeValue === "total" && this.targetValue > 0) {
        this.heroHeadlineTarget.innerHTML =
          `Here\u2019s what it takes to sign up <span class="text-primary-400">${fmt(members)}</span> members this month`
      } else {
        this.heroHeadlineTarget.innerHTML =
          `Here\u2019s what it takes to sign up <span class="text-primary-400">${fmt(members)}</span> more members`
      }
    }

    if (this.hasHeroSubTarget) {
      if (this.modeValue === "total" && this.targetValue > 0) {
        this.heroSubTarget.innerHTML =
          `The full funnel to hit your goal of <strong class="text-gray-200">${fmt(this.targetValue)}</strong> sign-ups this month.`
      } else if (this.targetValue > 0 && this.currentValue > 0) {
        this.heroSubTarget.innerHTML =
          `You\u2019re projecting <strong class="text-gray-200">${fmt(this.currentValue)}</strong> sign-ups \u2014 your goal is <strong class="text-gray-200">${fmt(this.targetValue)}</strong>. Here\u2019s the funnel to close that gap.`
      }
    }
  }

  updateToggleUI() {
    this.modeToggleTargets.forEach((btn) => {
      const on = btn.dataset.mode === this.modeValue
      btn.classList.toggle("bg-gray-700", on)
      btn.classList.toggle("text-white", on)
      btn.classList.toggle("text-gray-500", !on)
    })
  }

  setText(targets, val) {
    targets.forEach((el) => { el.textContent = val })
  }

  setWidth(targets, pct) {
    targets.forEach((el) => { el.style.width = `${Math.max(pct, 8)}%` })
  }
}
